import { useState, useEffect, useMemo, useCallback } from "react";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import {
  getTimeSlots,
  getWeekSchedule,
  getWeekSessions,
  createSession,
  updateSession,
  updateSessionStatus,
  deleteSession,
  duplicateSession,
  getTeacherZone,
  getHolidays,
} from "../../lib/api";

// Default time slots configuration
const DEFAULT_TIME_SLOTS = [
  { id: "slot_1", label: "08h30-09h15", start_time: "08:30", end_time: "09:15", order: 0 },
  { id: "slot_2", label: "09h15-10h00", start_time: "09:15", end_time: "10:00", order: 1 },
  { id: "slot_3", label: "10h15-11h00", start_time: "10:15", end_time: "11:00", order: 2 },
  { id: "slot_4", label: "11h00-11h45", start_time: "11:00", end_time: "11:45", order: 3 },
  { id: "slot_5", label: "13h30-14h15", start_time: "13:30", end_time: "14:15", order: 4 },
  { id: "slot_6", label: "14h15-15h00", start_time: "14:15", end_time: "15:00", order: 5 },
  { id: "slot_7", label: "15h15-16h00", start_time: "15:15", end_time: "16:00", order: 6 },
  { id: "slot_8", label: "16h00-16h30", start_time: "16:00", end_time: "16:30", order: 7 },
];

// Helper: Get week dates
export function getWeekDates(date, workingDays) {
  const monday = startOfWeek(date, { locale: fr, weekStartsOn: 1 });
  const days = [];
  const dayMap = { 0: "dimanche", 1: "lundi", 2: "mardi", 3: "mercredi", 4: "jeudi", 5: "vendredi", 6: "samedi" };
  
  for (let i = 0; i < 7; i++) {
    const d = addDays(monday, i);
    const dayName = dayMap[d.getDay()];
    days.push({
      date: format(d, "yyyy-MM-dd"),
      label: format(d, "EEEE", { locale: fr }),
      dayOfWeek: d.getDay(),
      isWorking: workingDays.includes(dayName),
    });
  }
  return days;
}

export function useJournalData() {
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
  const [workingDays, setWorkingDays] = useState(["lundi", "mardi", "jeudi", "vendredi"]);
  const [sessionsByDate, setSessionsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [franceCalendar, setFranceCalendar] = useState({ holidays: [], vacations: [] });
  const [teacherCycles, setTeacherCycles] = useState([]);

  // Fetch initial config
  const fetchConfig = useCallback(async () => {
    try {
      const [slotsRes, scheduleRes, zoneRes] = await Promise.all([
        getTimeSlots(),
        getWeekSchedule(),
        getTeacherZone(),
      ]);
      
      setTimeSlots(slotsRes.data?.slots || DEFAULT_TIME_SLOTS);
      setWorkingDays(scheduleRes.data?.working_days || ["lundi", "mardi", "jeudi", "vendredi"]);
      
      // Get teacher cycles
      const cycles = zoneRes.data?.cycles || [];
      setTeacherCycles(cycles);
      
      // Fetch holidays calendar
      const zone = zoneRes.data?.zone || "B";
      const calRes = await getHolidays(zone, new Date().getFullYear());
      setFranceCalendar({
        holidays: calRes.data?.public_holidays || [],
        vacations: calRes.data?.vacations || [],
      });
    } catch (err) {
      console.error("Error fetching config:", err);
    }
  }, []);

  // Fetch week data
  const fetchWeekData = useCallback(async () => {
    if (weekDates.length === 0) return;
    
    setLoading(true);
    try {
      const mondayStr = weekDates.find((d) => d.dayOfWeek === 1)?.date;
      if (!mondayStr) return;
      
      const { data } = await getWeekSessions(mondayStr);
      setSessionsByDate(data?.sessions_by_date || {});
    } catch (err) {
      console.error("Error fetching week sessions:", err);
      toast.error("Erreur lors du chargement des séances");
    } finally {
      setLoading(false);
    }
  }, [weekDates]);

  // Update week dates when current date or working days change
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    setWeekDates(getWeekDates(currentDate, workingDays));
  }, [currentDate, workingDays]);

  useEffect(() => {
    if (weekDates.length === 0) return;
    fetchWeekData();
  }, [weekDates, fetchWeekData]);

  // Navigation
  const goToPrevWeek = useCallback(() => {
    setCurrentDate((prev) => subWeeks(prev, 1));
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentDate((prev) => addWeeks(prev, 1));
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToDate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  // Get date status (holiday, vacation, etc.)
  const getDateStatus = useCallback(
    (dateStr) => {
      const holiday = franceCalendar.holidays.find((h) => h.date === dateStr);
      if (holiday) {
        return { isHoliday: true, name: holiday.name, type: "holiday" };
      }

      const vacation = franceCalendar.vacations.find(
        (v) => dateStr >= v.start && dateStr <= v.end
      );
      if (vacation) {
        return { isHoliday: true, name: vacation.name, type: "vacation" };
      }

      return { isHoliday: false, name: null, type: null };
    },
    [franceCalendar]
  );

  // Session actions
  const handleStatusChange = useCallback(
    async (sessionId, newStatus) => {
      try {
        await updateSessionStatus(sessionId, newStatus);
        toast.success(`Statut mis à jour : ${newStatus}`);
        fetchWeekData();
      } catch (err) {
        console.error("Error updating status:", err);
        toast.error("Erreur lors de la mise à jour");
      }
    },
    [fetchWeekData]
  );

  const handleDelete = useCallback(
    async (sessionId) => {
      if (!window.confirm("Supprimer cette séance ?")) return;
      try {
        await deleteSession(sessionId);
        toast.success("Séance supprimée");
        fetchWeekData();
      } catch (err) {
        console.error("Error deleting session:", err);
        toast.error("Erreur lors de la suppression");
      }
    },
    [fetchWeekData]
  );

  const handleDuplicate = useCallback(
    async (sessionId) => {
      try {
        await duplicateSession(sessionId);
        toast.success("Séance dupliquée");
        fetchWeekData();
      } catch (err) {
        console.error("Error duplicating session:", err);
        toast.error("Erreur lors de la duplication");
      }
    },
    [fetchWeekData]
  );

  const handleSaveSession = useCallback(
    async (sessionData, isEdit = false, sessionId = null) => {
      try {
        if (isEdit && sessionId) {
          await updateSession(sessionId, sessionData);
          toast.success("Séance modifiée");
        } else {
          await createSession(sessionData);
          toast.success("Séance créée");
        }
        fetchWeekData();
        return true;
      } catch (err) {
        console.error("Error saving session:", err);
        toast.error("Erreur lors de l'enregistrement");
        return false;
      }
    },
    [fetchWeekData]
  );

  // Week label
  const weekLabel = useMemo(() => {
    if (weekDates.length === 0) return "";
    const monday = weekDates.find((d) => d.dayOfWeek === 1);
    const friday = weekDates.find((d) => d.dayOfWeek === 5);
    if (!monday || !friday) return "";
    return `Semaine du ${format(new Date(monday.date), "d MMMM", { locale: fr })} au ${format(
      new Date(friday.date),
      "d MMMM yyyy",
      { locale: fr }
    )}`;
  }, [weekDates]);

  return {
    // State
    currentDate,
    weekDates,
    timeSlots,
    workingDays,
    sessionsByDate,
    loading,
    franceCalendar,
    teacherCycles,
    weekLabel,
    
    // Setters
    setTimeSlots,
    setWorkingDays,
    
    // Navigation
    goToPrevWeek,
    goToNextWeek,
    goToToday,
    goToDate,
    
    // Data
    fetchWeekData,
    getDateStatus,
    
    // Session actions
    handleStatusChange,
    handleDelete,
    handleDuplicate,
    handleSaveSession,
  };
}

export default useJournalData;
